/**
 * The component displaying the list of available modules.
 *
 * Copyright © Ircam Amplify 2024-2026
 */
import IconButton from '@/components/Button/IconButton'
import RectRoundButton from '@/components/Button/RectRoundButton'
import { ModuleConfig } from '@/components/ModuleConfig/ModuleConfig'
import ModuleConfigList, {
  ModuleOrder,
} from '@/components/ModuleConfig/ModuleConfigList'
import {
  getLayoutState,
  getUserState,
  setModuleSelectorCurrentPageId,
  useDispatch,
  useSelector,
} from '@/store'
import { AccountUtil } from '@amplify3/duplo-back-end'
import { useEffect, useRef, useState } from 'react'
import { BiLeftArrowAlt, BiRightArrowAlt } from 'react-icons/bi'
import { useNavigate } from 'react-router-dom'
import './ModuleSelector.css'

interface ModuleCardProps {
  title: string
  shortTitle?: string
  category?: string
  description?: string
  onClickFctn: () => void
}

function ModuleCard({
  title,
  shortTitle = '',
  category = '',
  description = '',
  onClickFctn = () => {},
}: ModuleCardProps) {
  return (
    <button type="button" className="moduleCard" onClick={onClickFctn}>
      <div className="moduleCardHeader">
        <div className="circleTag">{shortTitle}</div>
        <h4>{title}</h4>
        <div className="categoryTag">{category}</div>
      </div>
      <div className="moduleDescription">{description}</div>
    </button>
  )
}

const CARDS_PER_PAGE = 15

export const ModuleSelector = () => {
  const navigate = useNavigate()

  const modulesContainer = useRef(null)

  const dispatch = useDispatch()
  const { companyConsumption } = useSelector(getUserState)
  const { moduleSelectorCurrentPageId } = useSelector(getLayoutState)

  const [categories, setCategories] = useState<any>({
    VOICE: { selected: false },
    MUSIC: { selected: false },
    TOOLS: { selected: false },
  })
  const [cardList, setCardList] = useState<ModuleConfig[]>([])

  useEffect(() => {
    const activeFilters = Object.keys(categories).filter(
      (elt) => categories[elt].selected === true
    )

    setCardList(
      ModuleOrder.map((key) => ModuleConfigList[key])
        .filter((module) =>
          companyConsumption
            ? AccountUtil.isModuleAccessible(
                module.info.serviceName,
                companyConsumption.value
              )
            : false
        )
        .filter((module) =>
          activeFilters.length > 0
            ? activeFilters.includes(module.info.cat)
            : true
        )
    )
  }, [
    Object.keys(categories).filter((elt) => categories[elt].selected === true)
      .length,
    Object.keys(ModuleConfigList).length,
    Boolean(companyConsumption),
  ])

  useEffect(() => {
    const maximumPages = Math.ceil(cardList.length / CARDS_PER_PAGE)
    if (maximumPages > 0 && moduleSelectorCurrentPageId + 1 > maximumPages) {
      dispatch(setModuleSelectorCurrentPageId(maximumPages - 1))
    }
  }, [cardList.length, moduleSelectorCurrentPageId])

  const handleFilter = (c: any) => {
    const newCategories = { ...categories }
    newCategories[c].selected = !newCategories[c].selected
    setCategories(newCategories)
  }

  const resetFilter = (bool: boolean) => {
    const newCategories = { ...categories }
    Object.keys(newCategories).forEach((c) => {
      newCategories[c].selected = bool
    })
    setCategories(newCategories)
  }

  return (
    <div className="moduleSelector">
      <div className="filter-container">
        <div className="filter-container-header">
          <div className="filter-button-container">
            <div className="filter-title">Filters</div>
            <RectRoundButton
              id="filter-reset"
              label="Reset"
              onClick={() => resetFilter(false)}
            />
          </div>
          <div className="arrow-button-container">
            <IconButton
              icon={<BiLeftArrowAlt size="24px" />}
              disabled={
                !cardList[
                  moduleSelectorCurrentPageId * CARDS_PER_PAGE - CARDS_PER_PAGE
                ]
              }
              onClick={() =>
                dispatch(
                  setModuleSelectorCurrentPageId(
                    moduleSelectorCurrentPageId - 1
                  )
                )
              }
            />
            {moduleSelectorCurrentPageId + 1}/
            {cardList.length === 0
              ? 1
              : Math.ceil(cardList.length / CARDS_PER_PAGE)}
            <IconButton
              icon={<BiRightArrowAlt size="24px" />}
              disabled={
                !cardList[
                  moduleSelectorCurrentPageId * CARDS_PER_PAGE + CARDS_PER_PAGE
                ]
              }
              onClick={() =>
                dispatch(
                  setModuleSelectorCurrentPageId(
                    moduleSelectorCurrentPageId + 1
                  )
                )
              }
            />
          </div>
        </div>
        <div className="filter-item-container">
          {Object.keys(categories).map((c) => (
            <button
              key={c}
              className={
                categories[c].selected
                  ? 'categoryTag'
                  : 'categoryTag notSelected'
              }
              type="button"
              onClick={() => handleFilter(c)}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      <div className="modulesContainer" ref={modulesContainer}>
        {cardList
          .slice(
            moduleSelectorCurrentPageId * CARDS_PER_PAGE,
            moduleSelectorCurrentPageId * CARDS_PER_PAGE + CARDS_PER_PAGE
          )
          .map((module) => {
            return (
              <ModuleCard
                key={module.info.title}
                title={module.info.title}
                shortTitle={module.info.shortTitle}
                category={module.info.cat}
                description={module.info.description}
                onClickFctn={() =>
                  navigate(`/job-creation/${module.info.urlPathSegment}`)
                }
              />
            )
          })}
      </div>
    </div>
  )
}
